import { AppointmentType, BookingSource } from "@prisma/client";

// Consults (Marble consult + group consult) are not billed therapy, so they
// do not create the same-day claim-denial risk the client-already-booked
// guard exists to prevent. Overlapping times still conflict — a client
// cannot sit in two appointments at once.

export const NOT_BILLABLE_APPOINTMENT_TYPES: AppointmentType[] = [
  AppointmentType.GROUP_CONSULT,
  AppointmentType.CONSULT,
];

/**
 * Internal-facing display names for appointment types — what staff see in the
 * CRM, task notes and Slack alerts.
 *
 * In `@marble/core` because `workers/lambdas` names appointment types in the
 * task notes it writes for Care Ops, and `apps/web` names the same types all
 * over the CRM; a second copy would drift the moment a type is renamed.
 * Client-facing wording is separate and lives in the app
 * (`AppointmentTypeEnumToStrClientFacing`).
 */
export const AppointmentTypeEnumToStr: Record<AppointmentType, string> = {
  [AppointmentType.CONSULT]: "Consult",
  [AppointmentType.INTAKE]: "Intake",
  [AppointmentType.INDIVIDUAL_THERAPY]: "Individual Therapy",
  [AppointmentType.FAMILY_THERAPY]: "Family Therapy",
  [AppointmentType.GUARDIAN_CONSULT]: "Guardian Consult",
  [AppointmentType.GROUP_THERAPY]: "Group Therapy",
  [AppointmentType.GROUP_CONSULT]: "Group Leader Meet & Greet",
  [AppointmentType.MEDICATION_MANAGEMENT]: "Medication Management",
  [AppointmentType.MEDICATION_MANAGEMENT_INTAKE]:
    "Medication Management Intake",
};

/**
 * Staff-facing description of where an appointment was booked from, shown on
 * the admin appointment page and in the duplicate-intake Slack alert.
 */
export const BookingSourceDescription: Record<BookingSource, string> = {
  [BookingSource.UNKNOWN]: "Unknown source",
  [BookingSource.PATIENT_PORTAL]: "Booked through patient portal schedule page",
  [BookingSource.SELF_SERVE_LINK]:
    "Booked via shareable self-serve booking link",
  [BookingSource.GUARDIAN_PAGE]:
    "Booked from appointments card on guardian profile page",
  [BookingSource.STUDENT_APPOINTMENTS_PAGE]:
    "Booked from appointments tab on student profile page",
  [BookingSource.STUDENT_ONBOARDING_CHECKLIST]:
    "Booked from intake scheduling button in ops checklist card",
  [BookingSource.STUDENT_ONBOARDING_PRODUCT]:
    "Booked during student onboarding flow intake step",
  [BookingSource.INTAKE_INDIVIDUAL_SCHEDULING_CARD]:
    "Booked from scheduling card during intake appointment",
  [BookingSource.CLIENT_RESCHEDULE_BUTTON]:
    "Rescheduled by client from appointment detail page",
  [BookingSource.THERAPIST_RESCHEDULE_BUTTON]:
    "Rescheduled by therapist from appointment detail page",
  [BookingSource.THERAPIST_STUDENT_TABLE]:
    "Booked from schedule button in student table next appointment column",
  [BookingSource.THERAPIST_STUDENT_INTAKE_FORM]:
    "Booked from reschedule intake button on student intake form",
  [BookingSource.THERAPIST_APPOINTMENT_PAGE]:
    "Booked from new appointment button on therapist appointments page or appointments tab",
  [BookingSource.THERAPIST_NEXT_STEPS_CARD]:
    "Booked from schedule next appointment button in next steps card",
  [BookingSource.THERAPIST_FINALIZE_APPOINTMENT_FORM]:
    "Booked from schedule next appointment button in finalize appointment form",
  [BookingSource.CLIENT_APPOINTMENT_PAGE]:
    "Booked by client from the appointment detail page",
  [BookingSource.INTAKE_AUTO_REBOOKING]:
    "Auto-rebooked from therapist cancellation",
};

/**
 * Types that do not count as a client's first non-intake (in-care) session.
 * Consults and intake remain Care Ops work, so inbound routing stays on Care
 * Ops until a completed appointment of any other type exists.
 */
export const FIRST_NON_INTAKE_EXCLUDED_APPOINTMENT_TYPES: AppointmentType[] = [
  AppointmentType.INTAKE,
  AppointmentType.MEDICATION_MANAGEMENT_INTAKE,
  AppointmentType.CONSULT,
  AppointmentType.GUARDIAN_CONSULT,
  AppointmentType.GROUP_CONSULT,
];

/**
 * Cancellation-reason strings written by the app's auto-cancel and Headway
 * setup flows. In `@marble/core` because both the app's appointment-status
 * badge and the voice-confirmation-call ETL report classify cancellations by
 * matching against these exact strings; a second copy would drift the
 * moment either flow's wording changes.
 */
export const AUTO_CANCELLED_CANCELLATION_REASON = "Auto Cancelled";
export const AUTO_CANCELLED_CANCELLATION_REASON_INCOMPLETE_ONBOARDING =
  "Auto Cancelled - Incomplete Onboarding (cannot be rescheduled until complete)";
export const CANCELLATION_REASON_HEADWAY_SETUP_INCOMPLETE =
  "Headway Setup Incomplete (cannot be rescheduled until complete)";

/** Cancellation reasons for insurance refer-outs, written by the app and the refer-out Lambdas. */
export const CANCELLATION_REASON_INSURANCE_INELIGIBLE =
  "Insurance Ineligible (cannot be rescheduled until insurance is updated and verified)";
export const CANCELLATION_REASON_INACTIVE_INSURANCE =
  "Insurance Inactive (cannot be rescheduled until insurance is updated and verified)";
export const CANCELLATION_REASON_OON_INSURANCE =
  "Insurance is Out of Network (cannot be rescheduled until insurance is updated and verified)";

/** Cancellation reasons written when a client is opted out or referred out of care. */
export const CANCELLATION_REASON_OPTED_OUT = "Opted Out of Care";
export const CANCELLATION_REASON_REFERRED_OUT = "Referred Out of Care";

export const UNMARK_PAID_NO_SHOW_ERROR =
  "This no-show can no longer be unmarked because payment has already been issued";
